import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, CalendarDays, Check, X, MapPin,
  Mail, Phone, User, Clock, Trophy
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

export const AdminApprovalsPage = () => {
  const navigate = useNavigate();
  const { member } = useAuth();
  const [pendingVenues, setPendingVenues] = useState([]);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);

  const fetchPending = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/pending-approvals?admin_id=${member.id}`);
      setPendingVenues(res.data.pending_venues);
      setPendingEvents(res.data.pending_events);
    } catch {
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, [member?.id]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const approveVenue = async (venueId) => {
    setProcessingId(venueId);
    try {
      const res = await axios.put(`${API_URL}/admin/venues/${venueId}/approve`, { admin_id: member.id });
      toast.success(res.data.message);
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessingId(null);
    }
  };

  const rejectVenue = async (venueId) => {
    setProcessingId(venueId);
    try {
      await axios.put(`${API_URL}/admin/venues/${venueId}/reject`, { admin_id: member.id });
      toast.success('Venue rejected');
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessingId(null);
    }
  };

  const approveEvent = async (tournamentId, approved) => {
    setProcessingId(tournamentId);
    try {
      const res = await axios.put(`${API_URL}/admin/tournaments/${tournamentId}/approve-event`, {
        admin_id: member.id, approved
      });
      toast.success(res.data.message);
      fetchPending();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setProcessingId(null);
    }
  };

  const totalPending = pendingVenues.length + pendingEvents.length;

  return (
    <div className="min-h-screen bg-[#050505]">
      <nav className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-muted-foreground hover:text-white" data-testid="back-btn">
            <ArrowLeft className="mr-2 h-4 w-4" /> Dashboard
          </Button>
          <img src={TGA_LOGO} alt="TGA" className="h-10 object-contain cursor-pointer" onClick={() => navigate('/dashboard')} />
          <div className="w-24" />
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-heading text-white mb-2">Approvals</h1>
          <p className="text-muted-foreground">
            {totalPending > 0 ? `${totalPending} item${totalPending > 1 ? 's' : ''} pending review` : 'All caught up!'}
          </p>
        </div>

        <Tabs defaultValue="venues" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-muted mb-6">
            <TabsTrigger value="venues" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="venues-tab">
              Venues {pendingVenues.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-destructive rounded-full">{pendingVenues.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="events-tab">
              Events {pendingEvents.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 text-[10px] bg-destructive rounded-full">{pendingEvents.length}</span>}
            </TabsTrigger>
          </TabsList>

          {/* Venues Tab */}
          <TabsContent value="venues">
            {loading ? (
              <p className="text-muted-foreground text-center py-12">Loading...</p>
            ) : pendingVenues.length === 0 ? (
              <Card className="bg-card border-white/10">
                <CardContent className="p-12 text-center">
                  <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-heading text-white mb-2">No Pending Venues</h3>
                  <p className="text-muted-foreground text-sm">All venue applications have been reviewed.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingVenues.map(venue => (
                  <Card key={venue.id} className="bg-card border-white/10" data-testid={`venue-card-${venue.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        {venue.logo_url ? (
                          <img src={venue.logo_url} alt="" className="w-16 h-16 rounded-lg object-cover border border-white/10 shrink-0" />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center shrink-0">
                            <Building2 className="w-6 h-6 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-heading text-lg">{venue.name}</h3>
                          <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{venue.address}</span></div>
                            <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 shrink-0" />{venue.email}</div>
                            {venue.phone && <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 shrink-0" />{venue.phone}</div>}
                            <div className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 shrink-0" />{new Date(venue.created_at).toLocaleDateString()}</div>
                          </div>
                          {venue.description && <p className="text-muted-foreground text-sm mt-2 line-clamp-2">{venue.description}</p>}
                        </div>
                        <div className="flex gap-2 shrink-0 sm:flex-col">
                          <Button size="sm" onClick={() => approveVenue(venue.id)} disabled={processingId === venue.id} className="bg-secondary text-black hover:bg-secondary/90 text-xs" data-testid={`approve-venue-${venue.id}`}>
                            <Check className="w-3.5 h-3.5 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => rejectVenue(venue.id)} disabled={processingId === venue.id} className="text-destructive hover:bg-destructive/10 text-xs" data-testid={`reject-venue-${venue.id}`}>
                            <X className="w-3.5 h-3.5 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events">
            {loading ? (
              <p className="text-muted-foreground text-center py-12">Loading...</p>
            ) : pendingEvents.length === 0 ? (
              <Card className="bg-card border-white/10">
                <CardContent className="p-12 text-center">
                  <CalendarDays className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-heading text-white mb-2">No Pending Events</h3>
                  <p className="text-muted-foreground text-sm">All venue-submitted events have been reviewed.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {pendingEvents.map(event => (
                  <Card key={event.id} className="bg-card border-white/10" data-testid={`event-card-${event.id}`}>
                    <CardContent className="p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                        <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center shrink-0">
                          <Trophy className="w-5 h-5 text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-heading text-lg">{event.name}</h3>
                          <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                            {event.venue_name_display && <div className="flex items-center gap-2"><Building2 className="w-3.5 h-3.5 shrink-0" />{event.venue_name_display}</div>}
                            {event.venue_address && <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{event.venue_address}</span></div>}
                            {event.event_date && <div className="flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5 shrink-0" />{new Date(event.event_date).toLocaleString()}</div>}
                            <div className="flex items-center gap-2"><User className="w-3.5 h-3.5 shrink-0" />{event.num_holes} holes, Par {event.total_par}</div>
                          </div>
                          {event.description && <p className="text-muted-foreground text-sm mt-2 line-clamp-2">{event.description}</p>}
                        </div>
                        <div className="flex gap-2 shrink-0 sm:flex-col">
                          <Button size="sm" onClick={() => approveEvent(event.id, true)} disabled={processingId === event.id} className="bg-secondary text-black hover:bg-secondary/90 text-xs" data-testid={`approve-event-${event.id}`}>
                            <Check className="w-3.5 h-3.5 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => approveEvent(event.id, false)} disabled={processingId === event.id} className="text-destructive hover:bg-destructive/10 text-xs" data-testid={`reject-event-${event.id}`}>
                            <X className="w-3.5 h-3.5 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};
